import { action, computed, makeObservable, observable } from 'mobx'
import { Observable } from 'rxjs'
import BookkeepingManagerViewModel from 'admin/bookkeeping/BookkeepingManagerViewModel'
import AdminBundleManagerViewModel from 'admin/bundle/BundleManagerViewModel'
import { BundleStore } from 'admin/bundle/BundleStore'
import AdminInventoryManagerViewModel from 'admin/inventory/InventoryManagerViewModel'
import ScopeChangeRequestsViewModel, {
  FetchJobs,
} from 'admin/scope-change-requests/ScopeChangeRequestsViewModel'
import StaffingViewModel from 'admin/staffing/components/Staffing/StaffingViewModel'
import { AlertFactory } from 'alerts/Alert'
import Member from 'iam/models/Member'
import { GetCurrentMember } from 'iam/SessionStore'
import {
  GetCurrentUserWorkspaceMemberships,
  FetchUserWorkspaceMemberships,
} from 'iam/WorkspaceMembershipStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import isBookkeepingEnabled from 'jobs/bookkeeping/BookkeepingFeatureToggle'
import JobStore from 'jobs/JobStore'
import { AccountMergeRequestsViewModel } from './account-linking/AccountMergeRequestsViewModel'
import { AccountMergeRequestStore } from './account-linking/AccountMergeRequestStore'
import { IAMAPI } from '../iam/API'
import { PayoutsAdminRootViewModel } from 'payouts/admin/PayoutsAdminRootViewModel'
import BookkeepingManagerStore from './BookkeepingManagerStore'
import InventoryStore from './InventoryStore'
import { SupportingItemsPorts } from './ports'
import ScopeChangeRequestStore from './ScopeChangeRequestStore'
import WorkspacePostingStore from './WorkspacePostingStore'
import { getFeatureToggleClient } from '../infra/featureToggles'

export default class AdminViewModel {
  /**
   * Whether the admin navigation is visible.
   */
  navVisible = true

  closeNav() {
    this.navVisible = false
  }

  toggleNavVisibility() {
    if (!this.navVisible) {
      this.scopeChangeRequestsVM.changeRequestDetailsVM.close()
      this.staffingVm?.staffingDetailsVM.close()
      this.bookkeepingManagerVM.detailsVM.close()
    }

    this.navVisible = !this.navVisible
  }

  constructor(
    private newAlert: AlertFactory,
    private inventoryStore: InventoryStore,
    private bundleStore: BundleStore,
    private scopeChangeRequestStore: ScopeChangeRequestStore,
    private fetchWorkspaceMemberships: FetchUserWorkspaceMemberships,
    private workspacePostingStore: WorkspacePostingStore | null,
    public payoutsAdminVm: PayoutsAdminRootViewModel | null,
    private bookkeepingManagerStore: BookkeepingManagerStore,
    private createFilterStorage: FilterStorageFactory,
    private fetchJobs: FetchJobs,
    private currentMember$: Observable<Member>,
    private getCurrentMember: GetCurrentMember,
    private supportingItemsAdapter: SupportingItemsPorts,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private jobStore: JobStore,
    private iamApi: IAMAPI,
  ) {
    makeObservable(this, {
      navVisible: observable,
      closeNav: action.bound,
      toggleNavVisibility: action.bound,
      scopeChangeRequestsCount: computed,
      isPlatformAdmin: computed,
      canManageJobScopeChangeRequest: computed,
      canManageServices: computed,
      canSeeAdminView: computed,
      canSeeStaffingManager: computed,
    })
  }

  inventoryManagerVm = new AdminInventoryManagerViewModel(
    this.newAlert,
    this.inventoryStore,
    this.getWorkspaceMemberships,
    this.createFilterStorage,
    this.supportingItemsAdapter,
  )

  bundleManagerVm = new AdminBundleManagerViewModel(
    this.newAlert,
    this.bundleStore,
    this.inventoryStore,
    this.getWorkspaceMemberships,
    this.createFilterStorage,
    this.currentMember$,
  )

  scopeChangeRequestsVM = new ScopeChangeRequestsViewModel(
    this.inventoryStore,
    this.scopeChangeRequestStore,
    this.fetchWorkspaceMemberships,
    this.getWorkspaceMemberships,
    this.createFilterStorage,
    this.fetchJobs,
    this.getCurrentMember,
    this.closeNav.bind(this),
    this.newAlert,
  )

  staffingVm = this.workspacePostingStore
    ? new StaffingViewModel(
        this.workspacePostingStore,
        this.fetchWorkspaceMemberships,
        this.getWorkspaceMemberships,
        this.createFilterStorage,
        this.getCurrentMember,
        this.newAlert,
        this.closeNav.bind(this),
        this.navVisible,
      )
    : null

  bookkeepingManagerVM = new BookkeepingManagerViewModel(
    this.bookkeepingManagerStore,
    this.fetchWorkspaceMemberships,
    this.getWorkspaceMemberships,
    this.createFilterStorage,
    this.closeNav.bind(this),
    this.newAlert,
    this.jobStore,
    this.inventoryStore,
    this.getCurrentMember,
  )

  accountMergeRequestStore = new AccountMergeRequestStore(
    this.iamApi.accountMerging,
  )

  accountMergeRequestsVM = new AccountMergeRequestsViewModel(
    this.accountMergeRequestStore,
    this.newAlert,
  )

  get scopeChangeRequestsCount() {
    return this.scopeChangeRequestStore.scopeChangeRequests.length
  }

  get isPlatformAdmin() {
    return this.getCurrentMember().user.platformAdmin
  }

  get canManageAccountMergeRequests() {
    return this.getWorkspaceMemberships().some((wm) =>
      wm.member.hasPermission('ADMIN_GLOBAL_MANAGE_ACCOUNT_MERGE_REQUESTS'),
    )
  }

  get canManageJobScopeChangeRequest() {
    const currentMember = this.getCurrentMember()

    const hasPermissionInSomeWorkspace = this.getWorkspaceMemberships().some(
      (wms) => wms.member.hasPermission('ADMIN_JOB_SCOPE_CHANGE_MANAGE'),
    )

    return currentMember.user.platformAdmin || hasPermissionInSomeWorkspace
  }

  get canManageServices() {
    const hasPermissionInSomeWorkspace = this.getWorkspaceMemberships().some(
      (wms) =>
        wms.member.hasSomePermissions(
          'ADMIN_MANAGE_GLOBAL_INVENTORY_ITEMS',
          'MANAGE_WORKSPACE_INVENTORY_ITEMS',
        ),
    )

    return hasPermissionInSomeWorkspace
  }

  get canManageBookkeeping() {
    return (
      isBookkeepingEnabled() &&
      this.getWorkspaceMemberships().some((wms) =>
        wms.member.hasPermission('MANAGE_BOOKKEEPING'),
      )
    )
  }

  get canManagePayouts() {
    return Boolean(this.payoutsAdminVm) && this.isPlatformAdmin
  }

  get canSeeAdminView() {
    return (
      this.canManageJobScopeChangeRequest ||
      this.canManageServices ||
      this.canManageBookkeeping ||
      this.canSeeStaffingManager ||
      this.canManagePayouts ||
      this.canManageAccountMergeRequests
    )
  }

  get canSeeStaffingManager() {
    return this.getWorkspaceMemberships().some(
      (m) =>
        m.member.hasPermission('ADMIN_STAFFING_MANAGE') &&
        m.workspace.config.staffing_config?.enabled,
    )
  }

  activate() {
    if (this.canManageBookkeeping) {
      this.bookkeepingManagerVM?.activate()
    }
  }

  deactivate() {
    if (this.canManageBookkeeping) {
      this.bookkeepingManagerVM?.desactivate()
    }
  }
}
